/**
 * @fileoverview Triagem de um ticket: a ativa (itens, progresso e linha do
 * tempo) e as anteriores. Relê quando chega o aviso da triagem deste ticket
 * (socket da empresa, que só traz ids e status; os dados vêm da API).
 *
 * Também acompanha, pelo socket de tickets, a pausa e o dono da conversa: a
 * tela do ticket aberto não aplica esses avisos, e o painel precisa saber se
 * a IA está com a conversa.
 */

import { useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

const EMPTY = { loading: false, active: null, history: [] };

const useTicketTriage = (ticketId, enabled) => {
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const companyId = user?.companyId;
  const [state, setState] = useState(EMPTY);
  // Pausa/dono/status da conversa vindos do socket (null: vale o ticket da tela).
  const [ticketLive, setTicketLive] = useState(null);
  // Número da última leitura: resposta atrasada não sobrescreve a mais nova.
  const requestRef = useRef(0);
  // Ticket mostrado agora: leitura pedida por outra conversa (depois de trocar) é ignorada.
  const ticketRef = useRef(ticketId);
  ticketRef.current = ticketId;

  const reload = useCallback(async () => {
    if (!ticketId || !enabled || ticketRef.current !== ticketId) return;
    const current = ++requestRef.current;
    const valida = () => current === requestRef.current && ticketRef.current === ticketId;
    try {
      const { data } = await api.get(`/tickets/${ticketId}/triage`);
      if (!valida()) return;
      setState({ loading: false, active: data?.active || null, history: data?.history || [] });
    } catch (err) {
      if (!valida()) return;
      const status = err?.response?.status;
      // Sem acesso (fila de outra pessoa) ou ticket sumiu: sem triagem.
      // Falha momentânea (5xx, rede): mantém o que já estava na tela.
      if (status === 403 || status === 404) setState(EMPTY);
      else setState((prev) => ({ ...prev, loading: false }));
    }
  }, [ticketId, enabled]);

  useEffect(() => {
    setTicketLive(null);
    if (!ticketId || !enabled || !companyId) {
      requestRef.current += 1;
      setState(EMPTY);
      return undefined;
    }
    setState({ ...EMPTY, loading: true });
    reload();

    const socket = socketManager.getSocket(companyId);
    let timer = null;
    const onTriage = (data) => {
      if (Number(data?.ticketId) !== Number(ticketId)) return;
      // Várias ações seguidas viram uma leitura só.
      clearTimeout(timer);
      timer = setTimeout(reload, 300);
    };
    const onTicket = (data) => {
      const t = data?.ticket;
      if (data?.action !== "update" || !t || Number(t.id) !== Number(ticketId)) return;
      setTicketLive({ flowPaused: !!t.flowPaused, userId: t.userId ?? null, status: t.status, updatedAt: t.updatedAt });
    };
    socket.on(`company-${companyId}-triage`, onTriage);
    socket.on(`company-${companyId}-ticket`, onTicket);
    return () => {
      clearTimeout(timer);
      requestRef.current += 1;
      // disconnect() do socket gerenciado só solta os ouvintes deste uso (a conexão continua).
      socket.disconnect();
    };
  }, [ticketId, enabled, companyId, socketManager, reload]);

  // Depois de uma ação, a resposta já traz a sessão atualizada: mostra na hora
  // (só se for da conversa que está na tela).
  const applySession = useCallback((session) => {
    if (!session || Number(session.ticketId) !== Number(ticketRef.current)) return;
    setState((prev) => (prev.active && prev.active.id === session.id ? { ...prev, active: session } : prev));
  }, []);

  return { ...state, ticketLive, reload, applySession };
};

export default useTicketTriage;
